import { FC, PropsWithChildren, ReactElement, useMemo } from 'react';
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
} from 'react-hook-form';
import { get } from 'lodash';
import ErrorMessage from './error-message';
import { useSchema } from '../contexts/useSchema';

const Addon: FC<PropsWithChildren> = ({ children }) => {
  if (!children) return null;

  return <span className="p-inputgroup-addon">{children}</span>;
};

const Label: FC<PropsWithChildren<{ htmlFor: string }>> = ({
  htmlFor,
  children,
}) => (
  <label htmlFor={htmlFor} className="block mb-2">
    {children}
  </label>
);

const InputLabel: FC<{
  fieldName: string;
  label: ReactElement | string | number;
}> = ({ fieldName, label }) => {
  const fieldDescription = useSchema()?.fields[fieldName];

  const isRequired = useMemo(
    () => !get(fieldDescription, 'optional'),
    [fieldDescription],
  );

  return (
    <Label htmlFor={fieldName}>
      <span className="m-0 bold-font-size">
        {isRequired && <span className="m-0 text-danger">* </span>}
        {label}
      </span>
    </Label>
  );
};

const InputEnhancements: FC<PropsWithChildren<ApplyInputDetailsProps>> = ({
  addonLeft,
  addonRight,
  children,
  label,
  withErrorMessage = true,
  containerClassName,
  fieldState,
  field,
  visible = true,
}) => {
  if (!visible) return;

  if (addonLeft || addonRight) {
    return (
      <div className={containerClassName}>
        {label && <InputLabel fieldName={field.name} label={label} />}
        <div className="p-inputgroup">
          <Addon>{addonLeft}</Addon>
          {children}
          <Addon>{addonRight}</Addon>
        </div>
        {withErrorMessage && <ErrorMessage fieldState={fieldState} />}
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      {label && <InputLabel fieldName={field.name} label={label} />}
      {children}
      {withErrorMessage && <ErrorMessage fieldState={fieldState} />}
    </div>
  );
};

export type FormInputEnhancements = {
  addonRight?: ReactElement | string | number;
  addonLeft?: ReactElement | string | number;
  label?: ReactElement | string | number;
  withErrorMessage?: boolean;
  containerClassName?: string;
  visible?: boolean;
};

type ApplyInputDetailsProps = FormInputEnhancements & {
  fieldState: ControllerFieldState;
  field: ControllerRenderProps<FieldValues, any>;
};

export default InputEnhancements;
